/**
 * Monthly Calendar Screen Component
 *
 * Full-screen monthly calendar grid container.
 *
 * Follows single responsibility principle: coordinates calendar display.
 */

import { useMemo } from 'react';
import { StyleSheet, View, RefreshControl } from 'react-native';
import { MonthlyCalendarView } from './MonthlyCalendarView';
import { MonthlyCalendarFormatter } from '@/lib/formatters/MonthlyCalendarFormatter';
import type { MonthlyCalendarScreenProps } from '@/types/monthly-calendar';

/**
 * MonthlyCalendarScreen Component
 *
 * Renders the full-screen monthly calendar grid.
 */
export function MonthlyCalendarScreen({
  allEventsRaw,
  selectedDate,
  onDateSelect,
  onMonthChange,
  onRefresh,
  isRefreshing,
  accentColor,
  onMenuPress,
  onTodayPress,
  onAddEventPress,
  showCalendarSelector,
  calendarKey,
  calendarMappings,
  familyMembers,
  meMemberId,
}: MonthlyCalendarScreenProps) {
  // Get today's date string
  const todayDate = useMemo(() => {
    return MonthlyCalendarFormatter.toDateKey(new Date());
  }, []);

  // Build color context for resolving family member colors
  const colorContext = useMemo(() => {
    if (!calendarMappings || !familyMembers || !meMemberId) {
      return undefined;
    }
    return { calendarMappings, familyMembers, meMemberId };
  }, [calendarMappings, familyMembers, meMemberId]);

  // Build markedDates object for react-native-calendars
  const markedDates = useMemo(() => {
    return MonthlyCalendarFormatter.buildMarkedDatesObject(
      allEventsRaw,
      selectedDate,
      todayDate,
      colorContext
    );
  }, [allEventsRaw, selectedDate, todayDate, colorContext]);

  return (
    <View style={styles.container}>
      {/* Full-screen Monthly Calendar Grid */}
      <View style={styles.calendarContainer}>
        <RefreshControl
          refreshing={isRefreshing}
          onRefresh={onRefresh}
          tintColor={accentColor}
        />
        <MonthlyCalendarView
          markedDates={markedDates}
          selectedDate={selectedDate}
          onDateSelect={onDateSelect}
          onMonthChange={onMonthChange}
          accentColor={accentColor}
          onMenuPress={onMenuPress}
          onTodayPress={onTodayPress}
          onAddEventPress={onAddEventPress}
          showCalendarSelector={showCalendarSelector}
          calendarKey={calendarKey}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  calendarContainer: {
    flex: 1,
  },
});
